/**
 * Leitura RASA da resposta do usuário no roteiro do orçamento.
 *
 * Espelho de `ia-chat-answer.parser.dart` (app) — os dois lados precisam
 * responder igual, senão o mesmo "Sim, taxa de R$ 10" avança no celular e
 * repergunta no navegador.
 *
 * Serve para uma coisa só: decidir se a próxima pergunta ainda faz sentido. Não
 * extrai valores — quem monta o orçamento é a IA, no worker do backend. Por isso
 * um falso negativo custa no máximo uma pergunta a mais, nunca um dado errado.
 */
export interface IaChatAnswer {
    /** O usuário disse que aquele tópico não existe ("não", "nenhuma taxa"). */
    isNegative: boolean;
    /** Disse que existe, sem dar o valor ("sim"). */
    isAffirmative: boolean;
    /** Apareceu um percentual ("10%", "10 por cento"). */
    hasPercentage: boolean;
    /** Apareceu um valor em dinheiro (símbolo ou nome da moeda). */
    hasCurrency: boolean;
    /** Apareceu algum número. */
    hasNumber: boolean;
    /** Já dá para saber se é percentual ou dinheiro — não pergunte o modo. */
    hasExplicitMode: boolean;
    /** Tem número, mas sem unidade ("taxa de visita, 50") — aí sim vale perguntar. */
    needsMode: boolean;
}

/**
 * Palavras que ABREM uma resposta negativa, por idioma. Só o primeiro token é
 * comparado: "vai ter taxa, mas sem desconto" não é negativa — a fala é sobre a
 * taxa. Sem isso, um "no" solto seria negativa em português, onde é preposição.
 */
const NEGATIVE_WORDS: Record<string, string[]> = {
    pt: ['nao', 'nenhum', 'nenhuma', 'sem', 'nada', 'zero'],
    en: ['no', 'none', 'nope', 'without', 'nothing', 'zero'],
    es: ['no', 'ninguno', 'ninguna', 'sin', 'nada', 'cero'],
    de: ['nein', 'kein', 'keine', 'ohne', 'nichts', 'null'],
    fr: ['non', 'aucun', 'aucune', 'sans', 'rien', 'zero'],
};

const AFFIRMATIVE_WORDS: Record<string, string[]> = {
    pt: ['sim', 'tem', 'vai', 'claro', 'positivo'],
    en: ['yes', 'yeah', 'yep', 'sure', 'there'],
    es: ['si', 'claro', 'seguro'],
    de: ['ja', 'klar', 'sicher'],
    fr: ['oui', 'ouais', 'bien'],
};

const PERCENT_WORDS = [
    'por cento',
    'porcento',
    'percent',
    'per cent',
    'prozent',
    'pour cent',
    'pourcent',
    'por ciento',
];

/** Nomes de moeda por código — o símbolo vem do documento/empresa. */
const CURRENCY_WORDS: Record<string, string[]> = {
    BRL: ['real', 'reais', 'conto'],
    USD: ['dolar', 'dolares', 'dollar', 'dollars', 'buck', 'bucks'],
    EUR: ['euro', 'euros'],
    GBP: ['libra', 'libras', 'pound', 'pounds', 'quid', 'pfund'],
};

/** Remove acentos e caixa para o match não depender de como o usuário digitou. */
function normalize(text: string): string {
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
}

export function parseIaChatAnswer(
    text: string,
    options: { currencySymbol: string; currencyCode: string; languageCode: string }
): IaChatAnswer {
    const empty: IaChatAnswer = {
        isNegative: false,
        isAffirmative: false,
        hasPercentage: false,
        hasCurrency: false,
        hasNumber: false,
        hasExplicitMode: false,
        needsMode: false,
    };

    const normalized = normalize(text).trim();
    if (!normalized) return empty;

    const language = options.languageCode.split(/[-_]/)[0].toLowerCase();
    // O usuário pode responder num idioma diferente do app: o do app manda, mas
    // o inglês entra junto porque "no"/"yes" aparecem em qualquer teclado.
    const negatives = new Set([
        ...(NEGATIVE_WORDS[language] ?? []),
        ...(language !== 'pt' && language !== 'fr' ? NEGATIVE_WORDS['en'] : []),
    ]);
    const affirmatives = new Set([...(AFFIRMATIVE_WORDS[language] ?? []), ...AFFIRMATIVE_WORDS['en']]);

    const words = normalized.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
    const firstWord = words[0] ?? '';

    const hasNumber = /\d/.test(normalized);
    const hasPercentage = normalized.includes('%') || PERCENT_WORDS.some(word => normalized.includes(word));

    const symbol = normalize(options.currencySymbol);
    const hasCurrency =
        (!!symbol && normalized.includes(symbol)) ||
        (CURRENCY_WORDS[options.currencyCode.toUpperCase()] ?? []).some(word => words.includes(word));

    // Uma negativa com número ("sem taxa, só os 500 do serviço") não é negativa
    // limpa — deixamos a IA resolver e seguimos sem marcar o tópico como vazio.
    const isNegative = negatives.has(firstWord) && !hasNumber;
    const isAffirmative = !isNegative && affirmatives.has(firstWord) && !hasNumber;
    const hasExplicitMode = hasPercentage || hasCurrency;

    return {
        isNegative,
        isAffirmative,
        hasPercentage,
        hasCurrency,
        hasNumber,
        hasExplicitMode,
        needsMode: hasNumber && !hasExplicitMode,
    };
}

/**
 * "Pode gerar o documento?" dito pelo usuário — leitura LOCAL, para o FALLBACK.
 *
 * Quem encerra o chat é a IA (`confirmedFinish`). Mas quando ela não responde
 * (rede, saldo, erro) a conversa cai no roteiro local — e, sem o botão
 * "Concluir", o usuário ficaria sem nenhuma forma de gerar o documento. Este é
 * o gatilho que segura esse caso.
 *
 * Espelho de `ia-chat-answer.parser.dart` (app).
 *
 * O viés é CONSERVADOR: um falso negativo custa o usuário repetir a frase; um
 * falso positivo gera o documento antes da hora e fecha o chat na cara dele.
 * Por isso as frases vêm em duas camadas — as que só podem ser um pedido ao
 * assistente valem em qualquer lugar do texto; as ambíguas ("terminei",
 * "acabei") só valem se forem a mensagem INTEIRA, porque neste chat elas quase
 * sempre falam do serviço ("acabei de trocar a resistência"), não da conversa.
 */
const FINISH_PHRASES: Record<string, string[]> = {
    pt: ['pode gerar', 'pode criar', 'pode concluir', 'pode montar', 'pode fazer o documento', 'gera o documento', 'cria o documento', 'monta o documento', 'ja terminamos', 'ja finalizamos', 'e so isso'],
    en: ['you can create', 'you can generate', 'go ahead', 'generate it', 'create it', 'generate the document', 'create the document', 'thats all', 'we are done', 'were done'],
    es: ['puedes crear', 'puedes generar', 'genera el documento', 'crea el documento', 'ya terminamos', 'eso es todo'],
    de: ['du kannst erstellen', 'erstelle das dokument', 'generiere das dokument', 'das ist alles', 'wir sind fertig'],
    fr: ['tu peux creer', 'tu peux generer', 'genere le document', 'cree le document', 'cest tout', 'nous avons fini'],
};

/** Ambíguas: só contam quando são a mensagem inteira (até 3 palavras). */
const FINISH_SHORT_PHRASES: Record<string, string[]> = {
    pt: ['terminei', 'ja terminei', 'acabei', 'ja acabei', 'finaliza', 'finalizar', 'concluir', 'conclui', 'pronto', 'so isso', 'e isso'],
    en: ['done', 'im done', 'i am done', 'finish', 'finished', 'finalize'],
    es: ['termine', 'ya termine', 'listo', 'finaliza', 'finalizar'],
    de: ['fertig', 'ich bin fertig', 'abschliessen', 'abschließen'],
    fr: ['fini', 'jai fini', 'termine', 'terminer'],
};

export function isIaChatFinishRequest(text: string, languageCode: string): boolean {
    const normalized = normalize(text)
        .replace(/['\u2019]/g, '')
        .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    if (!normalized) return false;

    const language = languageCode.split(/[-_]/)[0].toLowerCase();
    // O usuário pode escrever num idioma diferente do app — o inglês entra junto
    // porque "done"/"go ahead" aparecem em qualquer teclado.
    const phrases = [...(FINISH_PHRASES[language] ?? []), ...FINISH_PHRASES['en']];
    if (phrases.some(phrase => normalized.includes(phrase))) return true;

    if (normalized.split(' ').length > 3) return false;
    return [...(FINISH_SHORT_PHRASES[language] ?? []), ...FINISH_SHORT_PHRASES['en']].includes(normalized);
}
